import React from 'react';
import { Lottie } from 'lottie-react';
import { cn } from '@/lib/utils';

interface LoadingScreenProps {
  message?: string;
  className?: string;
  animationType?: 'spinner' | 'pulse' | 'default';
}

export const LoadingScreen: React.FC<LoadingScreenProps> = ({
  message = 'Loading...',
  className,
  animationType = 'spinner',
}) => {
  const animationSrc =
    animationType === 'pulse'
      ? '/lottie/loading-pulse.json'
      : '/lottie/loading-spinner.json';

  return (
    <div
      className={cn(
        'fixed inset-0 bg-white dark:bg-gray-900 flex flex-col items-center justify-center z-50',
        'bg-gradient-to-br from-orange-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900',
        className
      )}
    >
      <div className="flex flex-col items-center justify-center space-y-6">
        {/* Lottie Animation or Fallback */}
        <div className="relative">
          <Lottie src={animationSrc} loop autoplay className="w-32 h-32" />
          {/* Optional: Add a subtle shadow or glow effect */}
          <div className="absolute inset-0 bg-orange-500/20 rounded-full blur-xl scale-110"></div>
        </div>

        {/* Loading Text */}
        <div className="text-center space-y-2">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
            {message}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Please wait while we prepare everything for you
          </p>
        </div>

        {/* Progress Dots */}
        <div className="flex space-x-2">
          <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"></div>
          <div
            className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"
            style={{ animationDelay: '0.1s' }}
          ></div>
          <div
            className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"
            style={{ animationDelay: '0.2s' }}
          ></div>
        </div>
      </div>
    </div>
  );
};

// Alternative simpler loading spinner for inline use
export const LoadingSpinner: React.FC<{
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}> = ({ size = 'md', className }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  return (
    <div
      className={cn(
        'border-2 border-orange-200 border-t-orange-600 rounded-full animate-spin',
        sizeClasses[size],
        className
      )}
    />
  );
};
